import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBlueprintEvalsAuth } from '../hooks/useBlueprintEvalsAuth';
import './Quotes.css';

const API_HOST = process.env.REACT_APP_API_HOST || 'http://localhost:8080';

interface Turn {
  id: number;
  index: number;
  promptInput: string;
  modelId: string;
  returnJson: unknown;
  rawResponseText: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  latencyMs: number | null;
  thumbsDownBool: boolean;
  createdAt: string;
}

interface SessionDetail {
  id: number;
  draftQuoteObjectId: number | null;
  domainName: string;
  createdAt: string;
  archivedAt: string | null;
}

export default function BlueprintEvalSession() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { password, setPassword, authHeaders } = useBlueprintEvalsAuth();
  const [passwordInput, setPasswordInput] = useState('');
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async (pw: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_HOST}/api/blueprint-evals/sessions/${id}`, {
        headers: authHeaders(pw),
      });
      if (res.status === 401) {
        setPassword('');
        setError('Incorrect password.');
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setSession(data.session);
      setTurns(data.turns);
    } catch (err: any) {
      setError(err.message || 'Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (password) load(password);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, id]);

  // Plain-text transcript of the whole session, ordered like a chat log —
  // easy to paste into another tool (an LLM, a ticket, a doc) to dig into
  // why a generation went bad without clicking through every turn's JSON
  // separately.
  const buildTranscript = (): string => {
    if (!session) return '';
    const header = [
      `Blueprint AI session #${session.id} — ${session.domainName}`,
      `Draft quote object: ${session.draftQuoteObjectId ?? '—'}`,
      `Created: ${new Date(session.createdAt).toLocaleString()}`,
      `${turns.length} turn(s)${session.archivedAt ? ' · archived' : ''}`,
    ].join('\n');

    const turnBlocks = turns.map((t) => {
      const meta = [
        `Turn #${t.index}`,
        t.modelId,
        `${t.inputTokens ?? '—'} in / ${t.outputTokens ?? '—'} out tokens`,
        `${t.latencyMs ?? '—'} ms`,
        new Date(t.createdAt).toLocaleString(),
        t.thumbsDownBool ? 'FLAGGED 👎' : null,
      ]
        .filter(Boolean)
        .join(' · ');

      const lines = [`--- ${meta} ---`, '', 'USER:', t.promptInput, ''];
      if (t.returnJson) {
        lines.push('ASSISTANT (return JSON):', JSON.stringify(t.returnJson, null, 2), '');
      }
      if (t.rawResponseText) {
        lines.push('ASSISTANT (raw response text):', t.rawResponseText, '');
      }
      return lines.join('\n');
    });

    return [header, '', ...turnBlocks].join('\n');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildTranscript());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to copy to clipboard');
    }
  };

  const handleUnlock = (e: React.FormEvent) => {
    e.preventDefault();
    setPassword(passwordInput);
    setPasswordInput('');
  };

  const handleToggle = async (turn: Turn) => {
    setUpdatingId(turn.id);
    try {
      const res = await fetch(`${API_HOST}/api/blueprint-evals/turns/${turn.id}/toggle-thumbs-down`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to toggle flag');
      }
      await load(password);
    } catch (err: any) {
      setError(err.message || 'Failed to toggle flag');
    } finally {
      setUpdatingId(null);
    }
  };

  if (!password) {
    return (
      <div className="quotes-page">
        <div className="page-header">
          <div>
            <h1>Blueprint AI Evals</h1>
            <p className="subtitle">Enter the eval password to continue</p>
          </div>
        </div>
        <div className="page-content">
          <form onSubmit={handleUnlock} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <input
              type="password"
              className="filter-input"
              placeholder="Password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              autoFocus
            />
            <button type="submit" className="primary-button">Unlock</button>
          </form>
          {error && (
            <div className="error-message" style={{ marginTop: 12 }}>
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="quotes-page">
      <div className="page-header">
        <div>
          <h1>Session #{id}</h1>
          <p className="subtitle">
            <span
              style={{ color: '#3b82f6', cursor: 'pointer' }}
              onClick={() => navigate('/evals')}
            >
              ← Back to all turns
            </span>
          </p>
        </div>
        {session && (
          <button
            onClick={handleCopy}
            style={{ padding: '9px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', color: '#374151', cursor: 'pointer' }}
          >
            {copied ? 'Copied ✓' : 'Copy session interaction'}
          </button>
        )}
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}
        {loading && <div className="loading-message">Loading...</div>}

        {session && (
          <>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
              Domain: {session.domainName} · Draft quote object: {session.draftQuoteObjectId ?? '—'} · Created:{' '}
              {new Date(session.createdAt).toLocaleString()} · {turns.length} turn(s)
              {session.archivedAt ? ' · archived' : ''}
            </p>

            {turns.map((t) => (
              <div
                key={t.id}
                style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ color: '#6b7280', fontSize: 13 }}>
                    Turn #{t.index} · {t.modelId} · {t.inputTokens ?? '—'} in / {t.outputTokens ?? '—'} out tokens ·{' '}
                    {t.latencyMs ?? '—'} ms · {new Date(t.createdAt).toLocaleString()}
                    {t.thumbsDownBool && (
                      <span className="status-badge" style={{ backgroundColor: '#fee2e2', color: '#991b1b', marginLeft: 8 }}>
                        Flagged
                      </span>
                    )}
                  </div>
                  <button
                    disabled={updatingId === t.id}
                    onClick={() => handleToggle(t)}
                    style={{ padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', color: '#374151', cursor: 'pointer' }}
                  >
                    {t.thumbsDownBool ? 'Clear flag' : 'Flag 👎'}
                  </button>
                </div>

                <h4 style={{ margin: '8px 0 4px' }}>Prompt</h4>
                <pre style={preStyle}>{t.promptInput}</pre>

                <h4 style={{ margin: '12px 0 4px' }}>Return JSON</h4>
                <pre style={preStyle}>{t.returnJson ? JSON.stringify(t.returnJson, null, 2) : '—'}</pre>

                {t.rawResponseText && (
                  <details style={{ marginTop: 12 }}>
                    <summary style={{ cursor: 'pointer', color: '#6b7280' }}>Raw response text</summary>
                    <pre style={preStyle}>{t.rawResponseText}</pre>
                  </details>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

const preStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  background: '#f9fafb',
  padding: 12,
  borderRadius: 6,
  maxHeight: 420,
  overflow: 'auto',
  fontSize: 13,
};
